/**
 * slideService.ts
 * Office.js interactions for reading slide content and injecting
 * activity placeholder shapes.
 *
 * Uses PowerPoint.run() (Office.js Preview API set 1.2+) for shape
 * operations with a safe requireApiSet guard.
 */

import type { SlideShapeDescriptor, SlideTextContent } from '@types-local/office.types';
import type { ActivityConfig } from '@types-local/quiz.types';

function questionTextFor(config: ActivityConfig): string {
  if ('question' in config) return config.question;
  if ('prompt' in config) return config.prompt;
  if (config.type === 'video_quiz') return config.title ?? 'Interactive Video';
  return '';
}

// ---- API availability guard --------------------------------

/**
 * Returns true if the current Office host supports the PowerPoint
 * API requirement set needed for shape insertion.
 */
export function supportsShapeInsertion(): boolean {
  return (
    Office.context.requirements.isSetSupported('PowerPointApi', '1.2')
  );
}

/**
 * Captures the given slide as a base64 PNG (no data: prefix) for mirroring
 * to students. Requires PowerPointApi 1.8; returns null if unsupported or
 * on any error so callers can degrade gracefully.
 */
export async function getSlideImageBase64(
  slideIndex: number,
  width = 1024,
): Promise<string | null> {
  if (!Office.context.requirements.isSetSupported('PowerPointApi', '1.8')) {
    return null;
  }
  try {
    return await PowerPoint.run(async (context) => {
      const slide = context.presentation.slides.getItemAt(slideIndex - 1);
      const img = slide.getImageAsBase64({ width });
      await context.sync();
      return img.value;
    });
  } catch (err) {
    console.warn('[GuroHub] Slide image capture failed:', err);
    return null;
  }
}

// ---- Shape injection ---------------------------------------

/**
 * Injects a rounded-rectangle placeholder shape onto the current slide
 * as a visual hook for the activity button.
 *
 * Falls back to a no-op with a console warning if the API set is
 * unavailable (older Office versions).
 */
export async function injectActivityShape(
  descriptor: SlideShapeDescriptor,
  slideIndex: number,
): Promise<void> {
  if (!supportsShapeInsertion()) {
    console.warn('[GuroHub] Shape injection requires PowerPointApi 1.2+. Skipping.');
    return;
  }

  await PowerPoint.run(async (context) => {
    const slide = context.presentation.slides.getItemAt(slideIndex - 1);
    slide.load('shapes');
    await context.sync();

    // Insert a text box as the activity marker
    const shape = slide.shapes.addTextBox(descriptor.textLabel, {
      left: descriptor.left,
      top: descriptor.top,
      width: descriptor.width,
      height: descriptor.height,
    });

    // Name the shape so we can find it later
    shape.name = descriptor.name;

    // Style the shape
    shape.fill.setSolidColor(descriptor.fillColor);
    shape.lineFormat.visible = false;

    await context.sync();
  });
}

/**
 * Removes an activity shape by name from the current active slide.
 */
export async function removeActivityShape(shapeName: string): Promise<void> {
  if (!supportsShapeInsertion()) return;

  await PowerPoint.run(async (context) => {
    const slide = context.presentation.slides.getItemAt(0);
    slide.load('shapes/name');
    await context.sync();

    for (const shape of slide.shapes.items) {
      if (shape.name === shapeName) {
        shape.delete();
        break;
      }
    }

    await context.sync();
  });
}

const REVEAL_PREFIX = 'IQ_REVEAL_';

/**
 * Writes the full question (and answer options, for Multiple Choice)
 * onto the given slide as real PowerPoint text box shapes, visible in
 * native Slide Show mode — no add-in overlay required.
 *
 * Called when "Start Presenting" is clicked, so the question only
 * becomes visible to the class at presentation time. Safe to call
 * repeatedly: previously revealed shapes for the same activity are
 * replaced rather than duplicated.
 */
export async function revealQuizOnSlide(
  slideIndex: number,
  config: ActivityConfig,
  baseShapeName: string,
): Promise<void> {
  if (!supportsShapeInsertion()) {
    console.warn('[GuroHub] Shape reveal requires PowerPointApi 1.2+. Skipping.');
    return;
  }

  const revealName = `${REVEAL_PREFIX}${baseShapeName}`;

  await PowerPoint.run(async (context) => {
    const slide = context.presentation.slides.getItemAt(slideIndex - 1);
    slide.load('shapes/name');
    await context.sync();

    // Clear any previously revealed shapes for this activity before redrawing
    for (const shape of slide.shapes.items) {
      if (shape.name.startsWith(revealName)) {
        shape.delete();
      }
    }
    await context.sync();

    const questionShape = slide.shapes.addTextBox(questionTextFor(config), {
      left: 60,
      top: 40,
      width: 840,
      height: 90,
    });
    questionShape.name = `${revealName}_Q`;
    // Transparent background (no dark box) with dark, bold text so it reads
    // cleanly over any slide. fill.clear() removes the background entirely.
    questionShape.fill.clear();
    questionShape.lineFormat.visible = false;
    questionShape.textFrame.textRange.font.color = '#1E293B';
    questionShape.textFrame.textRange.font.size = 30;
    questionShape.textFrame.textRange.font.bold = true;

    if (config.type === 'multiple_choice') {
      const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F'];
      const startTop = 150;
      const rowHeight = 70;

      config.options.forEach((opt, i) => {
        const optionShape = slide.shapes.addTextBox(`${OPTION_LABELS[i]}.  ${opt.text}`, {
          left: 60,
          top: startTop + i * rowHeight,
          width: 840,
          height: 56,
        });
        optionShape.name = `${revealName}_OPT_${i}`;
        // Light chip instead of a dark box; keep a subtle border for structure.
        optionShape.fill.setSolidColor('#F1F5F9');
        optionShape.lineFormat.color = '#CBD5E1';
        optionShape.lineFormat.weight = 1;
        optionShape.textFrame.textRange.font.color = '#1E293B';
        optionShape.textFrame.textRange.font.size = 22;
      });
    }

    await context.sync();
  });
}

const LEADERBOARD_PREFIX = 'IQ_LEADERBOARD_';

export interface LeaderboardEntry {
  name: string;
  score: number;   // cumulative session points
  correct: number; // number of correct answers
}

/**
 * Writes a top-N leaderboard onto the given slide as real shapes,
 * visible in native Slide Show mode. Replaces any prior leaderboard
 * shapes so it can be refreshed after each scored question.
 */
export async function writeLeaderboardToSlide(
  slideIndex: number,
  entries: LeaderboardEntry[],
  topN = 5,
): Promise<void> {
  if (!supportsShapeInsertion()) {
    console.warn('[GuroHub] Leaderboard reveal requires PowerPointApi 1.2+. Skipping.');
    return;
  }

  const top = entries.slice(0, topN);
  const MEDALS = ['🥇', '🥈', '🥉'];

  await PowerPoint.run(async (context) => {
    const slide = context.presentation.slides.getItemAt(slideIndex - 1);
    slide.load('shapes/name');
    await context.sync();

    for (const shape of slide.shapes.items) {
      if (shape.name.startsWith(LEADERBOARD_PREFIX)) shape.delete();
    }
    await context.sync();

    const title = slide.shapes.addTextBox('🏆 Leaderboard', {
      left: 60, top: 40, width: 400, height: 60,
    });
    title.name = `${LEADERBOARD_PREFIX}TITLE`;
    title.fill.setSolidColor('#1E1B4B');
    title.lineFormat.visible = false;
    title.textFrame.textRange.font.color = '#FFD700';
    title.textFrame.textRange.font.size = 28;
    title.textFrame.textRange.font.bold = true;

    top.forEach((entry, i) => {
      const rank = MEDALS[i] ?? `${i + 1}.`;
      const row = slide.shapes.addTextBox(
        `${rank}  ${entry.name}    ${entry.score} pts  ·  ${entry.correct} correct`,
        { left: 60, top: 120 + i * 60, width: 600, height: 48 },
      );
      row.name = `${LEADERBOARD_PREFIX}ROW_${i}`;
      row.fill.setSolidColor('#312E81');
      row.lineFormat.visible = false;
      row.textFrame.textRange.font.color = '#FFFFFF';
      row.textFrame.textRange.font.size = 20;
    });

    await context.sync();
  });
}

// ---- Slide text extraction ---------------------------------

/**
 * Extracts all text content from text frames on the given slide.
 * Used to feed context into the AI Quiz Builder.
 */
export async function extractSlideText(slideIndex: number): Promise<SlideTextContent> {
  if (!Office.context.requirements.isSetSupported('PowerPointApi', '1.2')) {
    // Older fallback: use document.getSelectedDataAsync
    return extractSlideTextFallback();
  }

  return PowerPoint.run(async (context) => {
    const slide = context.presentation.slides.getItemAt(slideIndex - 1);
    slide.load('shapes/textFrame/textRange/text,shapes/name');
    await context.sync();

    let title: string | null = null;
    const bodyLines: string[] = [];

    for (const shape of slide.shapes.items) {
      try {
        const text = shape.textFrame?.textRange?.text?.trim();
        if (!text) continue;

        // Heuristic: the shape named "Title..." or first large text box is the title
        if (
          shape.name.toLowerCase().includes('title') ||
          (title === null && text.length < 120)
        ) {
          title = text;
        } else {
          bodyLines.push(text);
        }
      } catch {
        // Some shapes may not have text frames — safely skip
      }
    }

    const allText = [title, ...bodyLines].filter(Boolean).join('\n');
    return { title, bodyLines, allText };
  });
}

/**
 * Fallback for older Office versions: reads selected data as text.
 */
function extractSlideTextFallback(): Promise<SlideTextContent> {
  return new Promise((resolve) => {
    Office.context.document.getSelectedDataAsync(
      Office.CoercionType.Text,
      (result) => {
        const allText =
          result.status === Office.AsyncResultStatus.Succeeded
            ? String(result.value)
            : '';
        resolve({ title: null, bodyLines: [allText], allText });
      },
    );
  });
}

// ---- Slide index -------------------------------------------

/**
 * Returns the 1-based index of the currently selected slide.
 */
export function getCurrentSlideIndex(): Promise<number> {
  return new Promise((resolve) => {
    Office.context.document.getSelectedDataAsync(
      Office.CoercionType.SlideRange,
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          // Fallback: assume slide 1
          resolve(1);
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const slideRange = result.value as any;
        const index: number = slideRange?.slides?.[0]?.index ?? 1;
        resolve(index);
      },
    );
  });
}

// ---- Activity shape descriptor factory ---------------------

/**
 * Builds a shape descriptor for a given activity type.
 * Positions the shape in the lower-right area of the slide.
 */
export function buildShapeDescriptor(
  config: ActivityConfig,
  shapeName: string,
): SlideShapeDescriptor {
  const ACTIVITY_COLORS: Record<string, string> = {
    multiple_choice: '#5660F7',
    word_cloud:      '#FF820A',
    short_answer:    '#22C55E',
    slide_drawing:   '#A855F7',
    media_upload:    '#3B82F6',
    video_quiz:      '#EF4444',
  };

  const ACTIVITY_LABELS: Record<string, string> = {
    multiple_choice: '📊 Multiple Choice',
    word_cloud:      '☁️ Word Cloud',
    short_answer:    '✏️ Short Answer',
    slide_drawing:   '🎨 Slide Drawing',
    media_upload:    '📸 Media Upload',
    video_quiz:      '🎬 Interactive Video',
  };

  return {
    name: shapeName,
    left: 560,
    top: 430,
    width: 160,
    height: 56,
    fillColor: ACTIVITY_COLORS[config.type] ?? '#5660F7',
    textLabel: ACTIVITY_LABELS[config.type] ?? '▶ Activity',
  };
}
